import { useState, useEffect, useRef } from "react";
import "./ConfluenceFolderPicker.css";
import { selectUser } from "../../redux/features/appData";
import { useSelector } from "react-redux";
import Modal from "../shared/Modal";
import {
  apiGetConfluenceFolderResources,
  apiGetConfluenceSpaces,
} from "../../services/DriveService";
import { DriveResource, ConfluenceDriveResource, MSDrive } from "winslow-core";
import { acceptableResourceMimeType } from "winslow-core";

export const MULTI_SELECT_KEY = "Meta";
export const MULTI_SELECT_KEY_ALT = "Control";

type ConfluenceFolderPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectedItems: (items: DriveResource[], includeSubfolders: boolean) => void;
  confluenceSpace: MSDrive | null;
  handleOpenConfluencePicker: () => void;
};

export const ConfluenceFolderPicker = ({
  isOpen,
  onClose,
  onSelectedItems,
  confluenceSpace,
  handleOpenConfluencePicker,
}: ConfluenceFolderPickerProps) => {
  const storeUser = useSelector(selectUser);
  const checkboxRef = useRef<{ [key: string]: HTMLImageElement }>({});
  const resourcesRef = useRef<ConfluenceDriveResource[]>([]);

  const [rootFolder, setRootFolder] = useState<ConfluenceDriveResource | null>(
    null
  );
  const [currentFolder, setCurrentFolder] =
    useState<ConfluenceDriveResource | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<
    ConfluenceDriveResource[]
  >([]);
  const [includeSubfolders, setIncludeSubfolders] = useState<boolean>(true);
  const [selectedItems, setSelectedItems] = useState<ConfluenceDriveResource[]>(
    []
  );
  const [multiKeyDown, setMultiKeyDown] = useState<boolean>(false);
  const [resources, setResources] = useState<ConfluenceDriveResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (storeUser && confluenceSpace && !rootFolder) {
      initializeRootFolder();
    }

    window.addEventListener("keydown", handleGlobalKeyDown);
    window.addEventListener("keyup", handleGlobalKeyUp);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
      window.removeEventListener("keyup", handleGlobalKeyUp);
    };
  }, [confluenceSpace]);

  useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  const initializeRootFolder = async () => {
    if (!storeUser || !confluenceSpace) return;
    setIsLoading(true);

    // Create a root folder representing the space
    const rootConfluenceResources = await apiGetConfluenceFolderResources({
      userId: storeUser.id,
      spaceId: confluenceSpace.id,
    });

    console.log("Root confluence resources:", rootConfluenceResources);

    if (!rootConfluenceResources) {
      setResources([]);
      setIsLoading(false);
      return;
    }

    if (rootConfluenceResources.length === 1) {
      setRootFolder(rootConfluenceResources[0]);
      setCurrentFolder(rootConfluenceResources[0]);
      await loadResources(rootConfluenceResources[0].id);
      setIsLoading(false);
      return;
    } else {
      console.log("Loading resources for root folder");
      await loadResources();
    }
  };

  const loadResources = async (folderId?: string) => {
    if (!storeUser || !confluenceSpace) return;
    setIsLoading(true);

    const confluenceResources = await apiGetConfluenceFolderResources({
      userId: storeUser.id,
      spaceId: confluenceSpace.id,
      folderId: folderId,
    });

    if (!confluenceResources) {
      setResources([]);
      setIsLoading(false);
      return;
    }

    // Sort resources - folders first, then pages
    const sortedResources = confluenceResources.sort(
      (a: ConfluenceDriveResource, b: ConfluenceDriveResource) => {
        if (
          a.mimeType === "application/vnd.confluence.folder" &&
          b.mimeType !== "application/vnd.confluence.folder"
        )
          return -1;
        if (
          b.mimeType === "application/vnd.confluence.folder" &&
          a.mimeType !== "application/vnd.confluence.folder"
        )
          return 1;
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }
    );

    setResources(sortedResources);
    setIsLoading(false);
  };

  const handleGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === MULTI_SELECT_KEY || e.key === MULTI_SELECT_KEY_ALT) {
      setMultiKeyDown(true);
    }

    if (!multiKeyDown && (e.key === "a" || e.key === "A")) {
      selectAllFiles();
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleGlobalKeyUp = (e: KeyboardEvent) => {
    if (e.key === MULTI_SELECT_KEY || e.key === MULTI_SELECT_KEY_ALT) {
      setMultiKeyDown(false);
    }
  };

  const selectAllFiles = () => {
    const currentResources = resourcesRef.current;
    setSelectedItems(currentResources);

    // Update checkbox refs
    currentResources.forEach((resource) => {
      if (checkboxRef.current[resource.id]) {
        checkboxRef.current[resource.id].src = "/check_on.svg";
      }
    });
  };

  const handleSelectFolder = () => {
    if (!currentFolder) return;
    onSelectedItems([currentFolder], includeSubfolders);
  };

  const handleSelectedFiles = () => {
    onSelectedItems(selectedItems, includeSubfolders);
    onClose();
  };

  const handleSelectParentFolder = async () => {
    console.log("Navigation history length:", navigationHistory.length);
    console.log("Current folder:", currentFolder);
    console.log("Root folder:", rootFolder);

    if (navigationHistory.length > 0) {
      const newHistory = [...navigationHistory];
      const previousFolder = newHistory.pop();
      console.log("Moving to previous folder:", previousFolder);

      setNavigationHistory(newHistory);
      setCurrentFolder(previousFolder || rootFolder);
      await loadResources(previousFolder?.id || rootFolder?.id);
      setSelectedItems([]);
    } else {
      console.log("Moving to root folder");

      //setCurrentFolder(rootFolder);
      //await loadResources(rootFolder.id);
      setSelectedItems([]);
      handleOpenConfluencePicker();
    }
  };

  const handleItemClick = async (resource: ConfluenceDriveResource) => {
    if (
      resource.childrenCount === 0 &&
      resource.mimeType !== "application/vnd.confluence.folder"
    ) {
      //toggle the item if it is already selected
      if (selectedItems.some((item) => item.id === resource.id)) {
        setSelectedItems(
          selectedItems.filter((item) => item.id !== resource.id)
        );
        if (checkboxRef.current[resource.id]) {
          checkboxRef.current[resource.id].src = "/check_off.svg";
        }
      } else {
        setSelectedItems([...selectedItems, resource]);
        if (checkboxRef.current[resource.id]) {
          checkboxRef.current[resource.id].src = "/check_on.svg";
        }
      }
      return;
    }

    setSelectedItems([]);
    if (currentFolder) {
      setNavigationHistory([...navigationHistory, currentFolder]);
    }
    setCurrentFolder(resource);
    await loadResources(resource.id);
  };

  const toggleItemSelection = (resource: ConfluenceDriveResource) => {
    if (selectedItems.some((item) => item.id === resource.id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== resource.id));
      if (checkboxRef.current[resource.id]) {
        checkboxRef.current[resource.id].src = "/check_off.svg";
      }
    } else {
      setSelectedItems([...selectedItems, resource]);
      if (checkboxRef.current[resource.id]) {
        checkboxRef.current[resource.id].src = "/check_on.svg";
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      style={null}
      onClose={onClose}
      canClose={false}
      allowClickout={true}>
      <div className="confluence-folder-picker-container">
        <div className="confluence-folder-picker-header">
          <div className="confluence-folder-picker-title">
            {confluenceSpace?.name || "Select Files"}
          </div>
          <div className="confluence-folder-picker-buttons">
            <img
              className="icon-close"
              onClick={onClose}
              src="/icons/close.svg"
              alt="Close"
            />
          </div>
        </div>

        {currentFolder && (
          <div className="confluence-folder-picker-back">
            <div className="confluence-folder-picker-back-container">
              <div
                className="confluence-folder-picker-arrow"
                onClick={handleSelectParentFolder}>
                <img src="/icons/arrow_left.png" alt="Back" />
              </div>

              <div className="confluence-folder-picker-subfolder-name">
                {currentFolder.name}
              </div>
              <div className="confluence-folder-picker-select-folder">
                <>
                  <div
                    className="confluence-folder-picker-select-include"
                    onClick={() => setIncludeSubfolders(!includeSubfolders)}>
                    <img
                      src={
                        includeSubfolders ? "/check_on.svg" : "/check_off.svg"
                      }
                      alt={
                        includeSubfolders
                          ? "Include subfolders"
                          : "Exclude subfolders"
                      }
                    />
                    &nbsp;include subfolders&nbsp;&nbsp;
                  </div>
                </>
                {selectedItems.length > 0 && (
                  <div className="confluence-folder-picker-select-include">
                    <button
                      id="confluence-folder-picker-select-button"
                      onClick={handleSelectedFiles}>
                      Select {selectedItems.length}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="confluence-folder-picker-divider"></div>

        {!isLoading ? (
          resources.length > 0 ? (
            <div className="confluence-folder-picker-contents">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className={`confluence-folder-picker-entry ${
                    selectedItems.some(
                      (selected) => selected.id === resource.id
                    )
                      ? "confluence-folder-picker-entry-selected"
                      : ""
                  } ${resource.childrenCount > 0 ? "has-children" : "no-children"}`}
                  onClick={() => handleItemClick(resource)}>
                  <div
                    className="confluence-folder-picker-checkbox"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleItemSelection(resource);
                    }}>
                    <img
                      ref={(el) =>
                        (checkboxRef.current[resource.id] =
                          el as HTMLImageElement)
                      }
                      src={
                        selectedItems.some((item) => item.id === resource.id)
                          ? "/check_on.svg"
                          : "/check_off.svg"
                      }
                      alt="Select item"
                    />
                  </div>
                  <div className="confluence-folder-picker-children-indicator">
                    {resource.childrenCount > 0 ||
                    resource.mimeType ===
                      "application/vnd.confluence.folder" ? (
                      <img
                        src="/icons/light-mode/right.svg"
                        alt="Has children"
                      />
                    ) : (
                      <span className="dot"></span>
                    )}
                  </div>
                  {resource.mimeType ===
                    "application/vnd.confluence.folder" && (
                    <div className="confluence-folder-picker-icon">
                      <img src="/icons/folder.svg" alt="Folder" />
                    </div>
                  )}
                  <div className="confluence-folder-picker-name">
                    {resource.name}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="confluence-folder-picker-contents">
              <div className="confluence-folder-picker-entry">
                No resources found
              </div>
            </div>
          )
        ) : (
          <div className="confluence-folder-picker-contents">
            <div className="confluence-folder-picker-entry">Loading...</div>
          </div>
        )}
      </div>
    </Modal>
  );
};
